/**
 * This file gets all of the location data for the maps & their corresponding PDFs
 */

import React, { createContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { get } from 'aws-amplify/api';
import { useParams } from 'react-router-dom';

import {
      Text,
      StyleSheet,
      View
    } from "@react-pdf/renderer";

import Union from "../components/maps/Union";
import Chestnut from "../components/maps/Chestnut";
import Castro from "../components/maps/Castro";
import Divisadero from "../components/maps/Divisadero";
import Fillmore from '../components/maps/Fillmore';
import Haight from '../components/maps/Haight';
import Lombard from "../components/maps/Lombard";
import TwentyFourth from '../components/maps/TwentyFourth';
import VanNess from '../components/maps/VanNess';
import Polk from '../components/maps/Polk';
import Mission from '../components/maps/Mission';
import Columbus from '../components/maps/Columbus';
import Valencia from '../components/maps/Valencia';
import Hayes from '../components/maps/Hayes';
import GearyBlvd from '../components/maps/GearyBlvd';
import GearySt from '../components/maps/GearySt';
import Grant from '../components/maps/Grant';
import Post from '../components/maps/Post';
import Presidio from '../components/maps/Presidio';
import Sacramento from '../components/maps/Sacramento';
import Clement from '../components/maps/Clement';
import Irving from '../components/maps/Irving';
import Jefferson from '../components/maps/Jefferson';
import Market from '../components/maps/Market';
import WPortal from '../components/maps/WPortal';


const styles = StyleSheet.create({
    cross: {
        fontFamily: "Helvetica",
        fontSize: '8px',
        flexDirection: 'row',
        display: "flex",
        alignSelf: 'flex-start',
    }
    });

const AddressStatsContext = createContext();
const LocationContext = createContext();

/**
 * This function gathers all of the address stats & returns a component that displays the address data for map PDFs
 */

export const AddressStatsProvider = ({ highlightAvailableIsChecked, highlightTempClosedIsChecked, addresses, highlightBuildingForSale, allTotalsAreChecked }) => {

  const [unavailableCount, setUnavailableCount] = useState(null);
  const [unavailablePerc, setUnavailablePerc] = useState(null);
  const [availableCount, setAvailableCount] = useState(null);
  const [availablePerc, setAvailablePerc] = useState(null);
  const [temporarilyClosedCount, setTemporarilyClosedCount] = useState(null);
  const [temporarilyClosedPerc, setTemporarilyClosedPerc] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [availableStyle, setAvailableStyle] = useState({ backgroundColor: "white" });
  const [tempClosedStyle, setTempClosedStyle] = useState({ backgroundColor: "white" });
  const totalText =  `Total Spaces: ${totalCount}`;
  const availableText = `Available: ${availableCount} Spaces (${availablePerc}%)`;
  const unavailableText = `Unavailable/Occupied: ${unavailableCount} Spaces (${unavailablePerc}%)`
  const tempClosedText = `Temp. Closed: ${temporarilyClosedCount} Spaces (${temporarilyClosedPerc}%)`;


  useEffect(() => {
    if (!addresses?.data) return;

    if (unavailableCount === null) {
      setUnavailableCount(addresses.data.opNum);
      setUnavailablePerc(parseFloat(((addresses.data.opNum / addresses.data.totalDocs) * 100).toFixed(1)));
    }

    if (availableCount === null) {
      setAvailableCount(addresses.data.notOpDocs);
      setAvailablePerc(parseFloat(((addresses.data.notOpDocs / addresses.data.totalDocs) * 100).toFixed(1)));
    }

    if (temporarilyClosedCount === null) {
      setTemporarilyClosedCount(addresses.data.temporarilyClosed);
      setTemporarilyClosedPerc(parseFloat(((addresses.data.temporarilyClosed / addresses.data.totalDocs) * 100).toFixed(1)));
    }

    if (totalCount === null) {
      setTotalCount(addresses.data.totalDocs);
    }
  }, [addresses?.data]);

  useEffect(() => {
    setAvailableStyle({ backgroundColor: highlightAvailableIsChecked ? "yellow" : "white" });
  }, [highlightAvailableIsChecked]);

  useEffect(() => {
    setTempClosedStyle({ backgroundColor: highlightTempClosedIsChecked ? "yellow" : "white" });
  }, [highlightTempClosedIsChecked]);

  console.log(addresses.data)

  return (
     <AddressStatsContext.Provider value={{
      unavailableCount, unavailablePerc,
      availableCount, availablePerc,
      temporarilyClosedCount, temporarilyClosedPerc,
      totalCount }}>
        {highlightAvailableIsChecked  && !allTotalsAreChecked && 
            <React.Fragment>
              <View style={[styles.cross, availableStyle]}>
                <Text>{availableText}</Text>
              </View>
              <View style={[styles.cross, tempClosedStyle]}>
                <Text>{tempClosedText}</Text>
              </View>
            </React.Fragment>
        }
        {allTotalsAreChecked ? (
            <React.Fragment>
                <View style={styles.cross}>
                  <Text style={styles.cross}>{totalText}</Text>
                </View>
                <View style={[styles.cross, availableStyle]}>
                  <Text>{availableText}</Text>
                </View>
                <Text style={styles.cross}>{unavailableText}</Text>
                <View style={[styles.cross, tempClosedStyle]}>
                  <Text>{tempClosedText}</Text>
                </View>
            </React.Fragment>
        ) : (
            <></>
        )}
    </AddressStatsContext.Provider>
  );
};
export const useAddressStats = () => React.useContext(AddressStatsContext);


/**
 * This portion gets all of the location data for the maps
 */
export const LocationProvider = ({ flipped, setFlipped, streetname, token, modalChange, setModalChange, allLocations, setAllLocations, dataLoading, setDataLoading}) => {

  // Render map component based on street name
  const mapProps = {
      flipped,
      setFlipped,
      modalChange,
      setModalChange,
      allLocations,
      dataLoading,
      setDataLoading
    }
  const renderMap = () => {
    switch (streetname) {
      case '24th Street':
        return <TwentyFourth {...mapProps} />;
      case 'Castro Street':
        return <Castro {...mapProps} />;
      case 'Chestnut Street':
        return <Chestnut {...mapProps} />;
      case 'Clement Street':
        return <Clement {...mapProps} />;
      case 'Columbus Avenue':
        return <Columbus {...mapProps} />;
      case 'Divisadero Street':
        return <Divisadero {...mapProps} />;
      case 'Fillmore Street':
        return <Fillmore {...mapProps} />;
      case 'Geary Boulevard':
        return <GearyBlvd {...mapProps} />;
      case 'Geary Street':
        return <GearySt {...mapProps} />;
      case 'Grant Avenue':
        return <Grant {...mapProps} />;
      case 'Haight Street':
        return <Haight {...mapProps} />;
      case 'Hayes Street':
          return <Hayes {...mapProps} />;
      case 'Irving Street':
        return <Irving {...mapProps} />;
      case 'Jefferson Street':
        return <Jefferson {...mapProps} />;
      case 'Lombard Street':
          return <Lombard {...mapProps} />;
      case 'Market Street':
        return <Market {...mapProps} />;
      case 'Mission Street':
          return <Mission {...mapProps} />;
      case 'Polk Street':
        return <Polk {...mapProps} />;
      case 'Post Street':
        return <Post {...mapProps} />;
      case 'Presidio Avenue':
        return <Presidio {...mapProps} />;
      case 'Sacramento Street':
        return <Sacramento {...mapProps} />;
      case 'Union Street':
        return <Union {...mapProps} />;
      case 'Van Ness Avenue':
        return <VanNess {...mapProps} />
      case 'Valencia Street':
          return <Valencia {...mapProps} />
      case 'W Portal Avenue':
        return <WPortal {...mapProps} />
      case undefined:
        return <Union {...mapProps} />;
    }
  };

  /**  HANDLES GETTING ALL LOCATION DATA  **/
  const addresses = useQuery(['data', streetname], () => {

      async function getData(){
          // Create a get request to get all location data
          try{
              const street_name = abbreviateStreetName(streetname);
              const data = get({
                  apiName: 'LocationAPI',
                  path: `/location/all-data?streetname=${encodeURIComponent(street_name)}`,
                  options: {
                      headers: {
                          Authorization: `Bearer ${token}`
                      }
                  }
              })

              const { body } = await data.response;
              const res = await body.json();
              console.log('Getting all location data GET call succeeded for Map');
              setModalChange(false);

              var locations = [];
              // Get all the addresses into an array of JSON objects
              for(var i = 0; i < res.addresses.length; i++) {
                  locations.push({address: res.addresses[i].address,
                                  name: res.addresses[i].businessName,
                                  id: res.addresses[i]._id});
              }
              setAllLocations(locations);

              return res;
          } catch(e){
              console.log('Getting all location data GET call failed for Map: ', e);
          }
      }

      if(token){
          return getData();
      }
  })

  // Set dataLoading to false for the map to appear
  useEffect(() => {
    if (allLocations.length > 500) {
      setTimeout(() => {
          setDataLoading(false);
      }, 3000);
    } else if (allLocations.length > 300) {
      console.log("indie 300")
      setTimeout(() => {
          setDataLoading(false);
      }, 3000);
    } else if (allLocations.length > 200) {
      setTimeout(() => {
          setDataLoading(false);
      }, 2000);
    } else if (allLocations.length > 0) {
      setDataLoading(false);
    }
    
  }, [allLocations]);

  // Dispatch map-loaded event for scroll
  useEffect(() => {
    if (!dataLoading) {
      window.dispatchEvent(new Event('map-loaded'));
      
    }
  }, [dataLoading]);

  return <LocationContext.Provider value={{addresses}}>{renderMap()}</LocationContext.Provider>;
}
export const useAddresses = () => React.useContext(LocationContext)

/**
 * HELPER FUNCTIONS
 */
export function abbreviateStreetName(streetName) {
        const abbreviations = {
            "Street": "St",
            "Avenue": "Ave",
            "Boulevard": "Blvd",
            "Place": "Pl",
            "Road": "Rd",
            "Drive": "Dr",
            "Court": "Ct",
            "Lane": "Ln",
            "Circle": "Cir",
            "Parkway": "Pkwy",
            "Square": "Sq"
        };

        if (!streetName || typeof streetName !== 'string') return "";

        const words = streetName.trim().split(" ");
        const lastWord = words[words.length - 1];

        if (abbreviations[lastWord]) {
            words[words.length - 1] = abbreviations[lastWord];
        }

        return words.join(" ");
    }